import {
  CurrentStep,
  Domain,
  DynamicPage,
  DynamicPageVariant,
  HtmlFinalStep,
  HtmlStepperForm,
  HtmlStepperFormData,
  HtmlStepperStep,
  Quiz,
  QuizForm,
  Website,
  WebsiteDataWithForms,
} from '@superfunnel/types';
import { applyExtraData } from '@superfunnel/utils/website';
import { doc, onSnapshot, QueryDocumentSnapshot } from 'firebase/firestore';
import {
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAgentChat } from '../../../contexts/AgentChatContext';
import { useDashboardContext } from '../../../contexts/DashboardContext';
import { useHtmlStepperUpdater } from '../../../contexts/HtmlStepperUpdaterContext';
import { UserContext } from '../../../contexts/UserContext';
import { db } from '../../../Firebase';
import { useHtmlStepperAgentSession } from '../../../hooks/useHtmlStepperAgentSession';
import { quizFormsQuery, quizRef } from '../../../services/QuizService';
import {
  emptyConverter,
  getDynamicPagePrimaryDomain,
  getDynamicPageRelevantDomains,
} from '../../../utils';
import { EditorDevice } from './useHtmlStepperEditorState';

export interface UseHtmlStepperEditorDataReturn {
  // Account ID
  accountId: string;

  // Quiz data
  quiz: Quiz | null;
  dynamicPage: DynamicPage | null;
  dynamicPageId: string | null;
  forms: QueryDocumentSnapshot<QuizForm>[];

  // Raw form from Firestore (unmodified by agent session)
  rawForm: HtmlStepperForm | null;
  setRawForm: (form: SetStateAction<HtmlStepperForm | null>) => void;

  // Computed form (may use agent session data when active)
  form: HtmlStepperForm | null;

  // Form ID
  formId: string | null;

  // Form data (development data from form, for editing)
  formData: HtmlStepperFormData | null;

  // Update form data (triggers persistence via updater)
  updateFormData: (data: SetStateAction<HtmlStepperFormData | null>) => void;

  // Raw form data (for reverting agent changes)
  rawFormData: HtmlStepperFormData | null;

  // Save status
  hasChanges: boolean;
  saving: boolean;
  lastSavedAt: Date | null;
  publishing: boolean;
  publish: (formId: string) => Promise<void>;
  discarding: boolean;
  discardChanges: (formId: string) => Promise<void>;
  canDiscard: boolean;

  // Undo/Redo
  canRevert: boolean;
  canForward: boolean;
  revertLatestUpdate: () => boolean;
  forwardRevert: () => boolean;

  // Agent session
  agentSession: ReturnType<typeof useHtmlStepperAgentSession>;

  // Current step data (derived from currentStep)
  currentStepData: HtmlStepperStep | null;
  currentFinalStepData: HtmlFinalStep | null;

  // Preview data (domain, variant, website)
  primaryDomain: QueryDocumentSnapshot<Domain> | undefined;
  variantId: string | null;
  websiteId: string | null;
  website: Website | null;
}

export interface UseHtmlStepperEditorDataProps {
  formId: string | null;
  quizId: string | null;
  currentStep: CurrentStep;
  device: EditorDevice;
}

export function useHtmlStepperEditorData({
  formId,
  quizId,
  currentStep,
  device,
}: UseHtmlStepperEditorDataProps): UseHtmlStepperEditorDataReturn {
  const { selectedAccount } = useContext(UserContext);

  // Get the updater context for persisting changes
  const {
    createStepperUpdate,
    applyFormChange,
    applyChangesSync,
    saveStatus,
    lastSavedAt,
    publishing,
    publish,
    discarding,
    discardChanges,
    setSaveStatus,
    clearHistory,
    canRevert,
    canForward,
    revertLatestUpdate,
    forwardRevert,
  } = useHtmlStepperUpdater();

  const { setAgentContext } = useAgentChat();

  // Quiz data
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [dynamicPage, setDynamicPage] = useState<DynamicPage | null>(null);
  const [variant, setVariant] = useState<DynamicPageVariant | null>(null);
  const [rawWebsite, setRawWebsite] = useState<Website | null>(null);
  const [forms, setForms] = useState<QueryDocumentSnapshot<QuizForm>[]>([]);

  // Raw form object fetched via onSnapshot (unmodified by agent session)
  const [rawForm, setRawForm] = useState<HtmlStepperForm | null>(null);

  // Track whether there are changes to publish (derived from raw form)
  const hasChangesToPublish = useMemo(
    () => quiz?.hasChangesToPublish ?? false,
    [quiz],
  );

  // Derive dynamicPageId from quiz
  const dynamicPageId = quiz?.linkedDynamicPageId ?? null;

  // Get domains from dashboard context for preview URL
  const { domains, accountBrand, accountConfig, globalSections } =
    useDashboardContext();

  // Get relevant domains for the dynamic page
  const relevantDomains = useMemo(() => {
    if (!domains || !dynamicPage) return domains;
    return getDynamicPageRelevantDomains(domains, dynamicPage);
  }, [domains, dynamicPage]);

  const primaryDomain = useMemo(() => {
    if (!relevantDomains) return;
    return getDynamicPagePrimaryDomain(relevantDomains);
  }, [relevantDomains]);

  // Subscribe to quiz document
  useEffect(() => {
    if (!selectedAccount || !quizId) return;

    const unsubscribe = onSnapshot(
      quizRef(selectedAccount.id, quizId),
      (snapshot) => {
        if (snapshot.exists()) {
          setQuiz(snapshot.data());
        }
      },
      (error) => {
        console.error('Error fetching quiz:', error);
      },
    );

    return () => unsubscribe();
  }, [selectedAccount, quizId]);

  // Subscribe to linked DynamicPage
  useEffect(() => {
    if (!selectedAccount || !dynamicPageId) {
      setDynamicPage(null);
      return;
    }

    const dynamicPageRef = doc(
      db,
      'accounts',
      selectedAccount.id,
      'campaigns',
      dynamicPageId,
    ).withConverter(emptyConverter<DynamicPage>());

    const unsubscribe = onSnapshot(
      dynamicPageRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setDynamicPage(snapshot.data());
        }
      },
      (error) => {
        console.error('Error fetching linked DynamicPage:', error);
      },
    );

    return () => unsubscribe();
  }, [selectedAccount, dynamicPageId]);

  // Subscribe to default variant
  useEffect(() => {
    if (!selectedAccount || !dynamicPageId) {
      setVariant(null);
      return;
    }

    const variantRef = doc(
      db,
      'accounts',
      selectedAccount.id,
      'campaigns',
      dynamicPageId,
      'variants',
      'default',
    ).withConverter(emptyConverter<DynamicPageVariant>());

    const unsubscribe = onSnapshot(
      variantRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setVariant(snapshot.data());
        }
      },
      (error) => {
        console.error('Error fetching variant:', error);
      },
    );

    return () => unsubscribe();
  }, [selectedAccount, dynamicPageId]);

  // Derive websiteId from variant (always single website for quizzes)
  const websiteId = variant?.websiteIds?.[0] ?? null;

  // Subscribe to the website
  useEffect(() => {
    if (!selectedAccount || !dynamicPageId || !websiteId) {
      setRawWebsite(null);
      return;
    }

    const websiteRef = doc(
      db,
      'accounts',
      selectedAccount.id,
      'campaigns',
      dynamicPageId,
      'websites',
      websiteId,
    ).withConverter(emptyConverter<Website>());

    const unsubscribe = onSnapshot(
      websiteRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setRawWebsite(snapshot.data());
        }
      },
      (error) => {
        console.error('Error fetching website:', error);
      },
    );

    return () => unsubscribe();
  }, [selectedAccount, dynamicPageId, websiteId]);

  // VariantId is always 'default' for quizzes
  const variantId = dynamicPageId ? 'default' : null;

  // Enrich website with account brand, config, and forms
  const website = useMemo(() => {
    if (!rawWebsite || !dynamicPage) return null;

    const enrichedWebsite = applyExtraData(
      rawWebsite,
      dynamicPage,
      accountBrand,
      accountConfig,
      globalSections?.map((gs) => ({ ...gs.data().production, id: gs.id })) ??
        [],
    );

    // Add form IDs
    enrichedWebsite.development.formIds = quizId ? [quizId] : [];
    enrichedWebsite.development.quizIds = quizId ? [quizId] : [];

    // Cast to WebsiteDataWithForms and add quizzes with embedded forms
    if (quizId && quiz) {
      const formsMap: Record<string, HtmlStepperForm> = {};
      for (const formDoc of forms) {
        formsMap[formDoc.id] = formDoc.data() as HtmlStepperForm;
      }

      (enrichedWebsite.development as WebsiteDataWithForms).quizzes = {
        [quizId]: {
          quizId,
          formIdsWeights: quiz.formIdsWeights ?? {},
          forms: formsMap,
        },
      };
    }

    return enrichedWebsite;
  }, [
    rawWebsite,
    dynamicPage,
    accountBrand,
    accountConfig,
    globalSections,
    forms,
    quiz,
    quizId,
  ]);

  // Subscribe to quiz forms collection
  useEffect(() => {
    if (!selectedAccount || !quizId) return;

    const unsubscribe = onSnapshot(
      quizFormsQuery(selectedAccount.id, quizId),
      (snapshot) => {
        setForms(snapshot.docs);
      },
      (error) => {
        console.error('Error fetching quiz forms:', error);
      },
    );

    return () => unsubscribe();
  }, [selectedAccount, quizId]);

  // Fetch form document via onSnapshot
  useEffect(() => {
    if (!selectedAccount || !formId || !quizId) return;

    // Forms are stored under the quiz: accounts/{accountId}/quizzes/{quizId}/forms/{formId}
    const formRef = doc(
      db,
      'accounts',
      selectedAccount.id,
      'quizzes',
      quizId,
      'forms',
      formId,
    ).withConverter(emptyConverter<HtmlStepperForm>());

    const unsubscribe = onSnapshot(formRef, (snapshot) => {
      if (!snapshot.exists()) return;
      setRawForm(snapshot.data());
    });

    return () => {
      unsubscribe();
    };
  }, [formId, quizId, selectedAccount]);

  // Reset on open
  useEffect(() => {
    setSaveStatus('none');
    clearHistory();
  }, [clearHistory, setSaveStatus]);

  // Update agent context when formId, currentStep, or device changes
  useEffect(() => {
    if (quizId && formId) {
      const newContext = {
        quizId,
        formId,
        currentStepId:
          currentStep?.type === 'step' || currentStep?.type === 'finalStep'
            ? currentStep.id
            : undefined,
        currentViewport: device,
      };
      setAgentContext(newContext);
    }
  }, [quizId, formId, currentStep, device, setAgentContext]);

  // Agent session for tracking changes
  const agentSession = useHtmlStepperAgentSession(formId ? { formId } : null);

  // Track when the last agent session change was completed (for 2s grace period)
  const lastChangeCompletedAt = useRef<number | null>(null);

  // Computed form - uses agent session data when active, otherwise raw form
  const [form, setForm] = useState<HtmlStepperForm | null>(null);

  // Update form based on rawForm and agent session data
  useEffect(() => {
    if (!rawForm) {
      return;
    }

    const agentSessionData = agentSession.agentSessionData;

    // Check if there are any pending changes in the agent session
    const hasAgentSessionChanges = Object.values(
      agentSessionData?.changes || {},
    ).some((change) => change.status === 'pending');

    // Update timestamp when changes complete
    if (!hasAgentSessionChanges && agentSessionData?.formData) {
      if (lastChangeCompletedAt.current === null) {
        lastChangeCompletedAt.current = Date.now();
      }
    } else if (hasAgentSessionChanges) {
      // Reset timestamp when new changes appear
      lastChangeCompletedAt.current = null;
    }

    // Use agent session data if:
    // 1. There are active pending changes, OR
    // 2. Changes completed less than 2 seconds ago
    const timeSinceCompletion = lastChangeCompletedAt.current
      ? Date.now() - lastChangeCompletedAt.current
      : Infinity;
    const shouldUseAgentSessionData =
      agentSessionData?.formData &&
      (hasAgentSessionChanges || timeSinceCompletion < 2000);

    let currentForm = rawForm;

    if (shouldUseAgentSessionData) {
      console.log(
        '🤖 Using agent session form data',
        hasAgentSessionChanges
          ? 'with pending changes'
          : `(${Math.round(2000 - timeSinceCompletion)}ms remaining)`,
      );
      currentForm = {
        ...rawForm,
        development: agentSessionData!.formData!,
      };
    }

    applyChangesSync(currentForm.development); // Ensure updater has latest changes
    setForm({ ...currentForm, development: { ...currentForm.development } });
  }, [rawForm, agentSession.agentSessionData, applyChangesSync]);

  // Form data - development data from the computed form
  const formData = useMemo(() => form?.development ?? null, [form]);

  // Raw form data (for reverting agent changes)
  const rawFormData = useMemo(() => rawForm?.development ?? null, [rawForm]);

  // Update form data - creates an update and applies it via the updater
  const updateFormData = useCallback(
    (data: SetStateAction<HtmlStepperFormData | null>) => {
      // Resolve the SetStateAction
      const newFormData = typeof data === 'function' ? data(formData) : data;

      if (!newFormData || !formId) return;

      const update = createStepperUpdate(
        formId,
        { type: 'formData' },
        undefined,
        newFormData,
        'replaceFormData',
        rawFormData ?? undefined,
      );

      if (update) {
        applyFormChange(update);
      }
    },
    [applyFormChange, createStepperUpdate, formData, formId, rawFormData],
  );

  // Saving state - derived from updater context
  const saving = saveStatus === 'saving';

  // Get current step data based on unified currentStep
  const currentStepData = useMemo(() => {
    if (!currentStep || currentStep.type !== 'step' || !formData) return null;
    return formData.steps.find((s) => s.id === currentStep.id) ?? null;
  }, [currentStep, formData]);

  // Get current final step data based on unified currentStep
  const currentFinalStepData = useMemo(() => {
    if (!currentStep || currentStep.type !== 'finalStep' || !formData)
      return null;
    return formData.finalSteps.find((s) => s.id === currentStep.id) ?? null;
  }, [currentStep, formData]);

  // Can discard only if there's a production version to revert to
  // Supports both new (Cloud Storage via productionStorageUrl) and legacy (inline production)
  const canDiscard =
    hasChangesToPublish &&
    !!(rawForm?.productionStorageUrl || rawForm?.production);

  const accountId = selectedAccount?.id ?? '';

  return {
    accountId,
    quiz,
    dynamicPage,
    dynamicPageId,
    forms,
    rawForm,
    setRawForm,
    form,
    formId,
    formData,
    updateFormData,
    rawFormData,
    hasChanges: hasChangesToPublish,
    saving,
    lastSavedAt,
    publishing,
    publish,
    discarding,
    discardChanges,
    canDiscard,
    canRevert,
    canForward,
    revertLatestUpdate,
    forwardRevert,
    agentSession,
    currentStepData,
    currentFinalStepData,
    primaryDomain,
    variantId,
    websiteId,
    website,
  };
}
